import { Popover } from '@/components/Popover';
import { LabelSelect } from '@/pages/Schedule/components/LabelSelect';
import { PillsList } from '@/pages/Schedule/components/Pills';
import { definitions as labelDefinitions } from '@/pages/Schedule/utils/labelDefinitions';
import type { Label, LabelType } from '@/types/gql.generated';
import { Button, Flex, type FlexProps, Text, useDisclosure } from '@/ui';
import { LabelProvider } from '../LabelProvider';

type Props = {
  labelType: LabelType;
  value: Label[];
  isReadOnly?: boolean;
  // Spacing between the icon and the control. Defaults to "3" to match the
  // EditEventModal fields; inline-card usage passes "2".
  gap?: FlexProps['gap'];
  onChange: (labels: Label[]) => void;
};

export const LabelInput = ({
  labelType,
  value,
  isReadOnly,
  gap = '3',
  onChange,
}: Props) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const lib = labelDefinitions[labelType];

  if (isReadOnly) {
    if (value.length === 0) {
      return null;
    }

    return (
      <Flex align="center" gap={gap} w="100%">
        {lib.icon}
        <PillsList labels={value} />
      </Flex>
    );
  }

  const trigger = (
    <Flex align="center" gap={gap} w="100%">
      {lib.icon}
      <Button
        borderRadius="md"
        flex="1"
        fontSize="md"
        fontWeight="normal"
        justifyContent="flex-start"
        px="2"
        size="sm"
        variant="ghost"
        onClick={onOpen}
      >
        {value.length > 0 ? (
          <PillsList labels={value} overflow="hidden" />
        ) : (
          <Text color="gray.500">{lib.title}</Text>
        )}
      </Button>
    </Flex>
  );

  return (
    <LabelProvider labelType={labelType}>
      <Popover isOpen={isOpen} trigger={trigger} onClose={onClose}>
        <LabelSelect
          selected={value}
          onSelect={(label) => onChange([...value, label])}
          onDeselect={(label) =>
            onChange(value.filter((l) => l.id !== label.id))
          }
          onUpdate={(label) =>
            onChange(value.map((l) => (l.id === label.id ? label : l)))
          }
        />
      </Popover>
    </LabelProvider>
  );
};
